import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../context/AuthContext";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  green: "#2A9D67",
  soft: "#F7F8FA",
};

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";
const COURSE_VIDEO_ID = "7lAyfWMhQgI";
const COURSE_PLAYLIST_ID = "PLVzK6TImyCTQ";
const COURSE_EMBED_URL = `https://www.youtube.com/embed/${COURSE_VIDEO_ID}?list=${COURSE_PLAYLIST_ID}&rel=0`;
const COURSE_YOUTUBE_URL = `https://www.youtube.com/watch?v=${COURSE_VIDEO_ID}&list=${COURSE_PLAYLIST_ID}`;

const paths = [
  {
    id: "start",
    label: "Estoy arrancando",
    short: "De cero a tener tu negocio listo.",
    icon: RocketLaunchRoundedIcon,
    title: "Primero dejá WebProfit listo para trabajar.",
    description:
      "No intentes configurar todo. Hacé lo mínimo importante, probalo y después seguí creciendo.",
    steps: [
      "Creá tu cuenta y tu proyecto.",
      "Completá nombre, logo y datos del negocio.",
      "Abrí el panel y recorré las herramientas principales.",
    ],
    action: "Abrir mi panel",
  },
  {
    id: "sell",
    label: "Quiero vender",
    short: "Página, catálogo y primera operación.",
    icon: StorefrontRoundedIcon,
    title: "Convertí tu negocio en algo que ya se pueda mostrar y vender.",
    description:
      "La meta no es que quede perfecto: la meta es que una persona pueda entrar, entender qué vendés y avanzar.",
    steps: [
      "Cargá tus productos o servicios principales.",
      "Revisá cómo se ve tu página desde el celular.",
      "Hacé una operación de prueba antes de compartirla.",
    ],
    action: "Preparar mi negocio",
  },
  {
    id: "manage",
    label: "Quiero ordenar el negocio",
    short: "Stock, movimientos, caja y control.",
    icon: PointOfSaleRoundedIcon,
    title: "Usá WebProfit como centro de operación, no como otra planilla más.",
    description:
      "Cuando ventas, dinero y stock viven en el mismo flujo, entendés qué pasó sin reconstruir el negocio a mano.",
    steps: [
      "Registrá compras, ventas, ingresos y egresos donde corresponden.",
      "Usá cajas y cuentas para saber dónde está realmente el dinero.",
      "Revisá movimientos y stock como historial del negocio.",
    ],
    action: "Ir a gestión",
  },
  {
    id: "grow",
    label: "Ya lo uso",
    short: "Mejorar, automatizar y crecer.",
    icon: AutoAwesomeRoundedIcon,
    title: "Cuando lo básico funciona, recién ahí optimizá.",
    description:
      "Sumá equipo, automatizaciones y más herramientas a medida que el negocio realmente las necesita.",
    steps: [
      "Ajustá permisos y accesos de tu equipo.",
      "Activá módulos sólo cuando resuelvan una necesidad concreta.",
      "Usá soporte si algo te frena: no pierdas horas adivinando.",
    ],
    action: "Seguir en mi panel",
  },
];

function scrollToCourse() {
  document.getElementById("curso")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function CoursePathButton({ item, active, onClick }) {
  const Icon = item.icon;

  return (
    <Button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        width: "100%",
        minHeight: { xs: 76, md: 88 },
        justifyContent: "flex-start",
        textAlign: "left",
        textTransform: "none",
        borderRadius: 3.5,
        px: { xs: 1.7, md: 2 },
        py: 1.35,
        color: active ? "#fff" : BRAND.dark,
        bgcolor: active ? BRAND.dark : "#fff",
        border: active ? "1px solid #19242F" : "1px solid rgba(25,36,47,.11)",
        boxShadow: active ? "0 14px 30px rgba(25,36,47,.16)" : "none",
        "&:hover": {
          bgcolor: active ? "#243442" : "#FFF8F5",
          borderColor: active ? "#243442" : "rgba(243,90,58,.35)",
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          mr: 1.35,
          bgcolor: active ? "rgba(243,90,58,.18)" : "rgba(243,90,58,.1)",
          color: active ? "#FF9D84" : BRAND.orange,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 950, fontSize: { xs: ".95rem", md: "1rem" }, lineHeight: 1.15 }}>
          {item.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.45,
            color: active ? "rgba(255,255,255,.68)" : "rgba(25,36,47,.56)",
            fontSize: ".76rem",
            lineHeight: 1.35,
          }}
        >
          {item.short}
        </Typography>
      </Box>
    </Button>
  );
}

export default function DocumentationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [activePathId, setActivePathId] = React.useState("start");
  const isAuthenticated = Boolean(user);
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const activePath = paths.find((item) => item.id === activePathId) || paths[0];

  React.useEffect(() => {
    document.title = "Curso rápido | WebProfit";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const openPanel = () => {
    if (loading) return;
    navigate(
      isAuthenticated
        ? "/dashboard"
        : referralCode
          ? `/registro?ref=${encodeURIComponent(referralCode)}`
          : "/registro",
    );
  };

  const primaryLabel = loading
    ? "Cargando..."
    : isAuthenticated
      ? "Abrir mi panel"
      : "Crear mi cuenta gratis";

  return (
    <Box component="main" sx={{ bgcolor: "#fff", color: BRAND.dark, overflowX: "hidden" }}>
      <Box
        component="section"
        sx={{
          pt: { xs: 13, sm: 14, md: 16 },
          pb: { xs: 5.5, md: 8 },
          px: { xs: 2, md: 4 },
          background:
            "radial-gradient(circle at 88% 8%, rgba(243,90,58,.2), transparent 26%), linear-gradient(180deg, #FFF8F5 0%, #FFFFFF 82%)",
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Stack alignItems="center" textAlign="center">
            <Chip
              icon={<PlayArrowRoundedIcon />}
              label="Curso rápido de WebProfit"
              sx={{
                bgcolor: "rgba(243,90,58,.1)",
                color: BRAND.orangeDark,
                fontWeight: 900,
                "& .MuiChip-icon": { color: BRAND.orange },
              }}
            />

            <Typography
              component="h1"
              sx={{
                mt: 2.2,
                maxWidth: 920,
                fontWeight: 950,
                letterSpacing: "-.055em",
                lineHeight: 0.98,
                fontSize: { xs: "2.45rem", sm: "3.5rem", md: "4.75rem" },
              }}
            >
              Aprendé WebProfit
              <Box component="span" sx={{ color: BRAND.orange }}> haciéndolo.</Box>
            </Typography>

            <Typography
              sx={{
                mt: 2,
                maxWidth: 690,
                color: "rgba(25,36,47,.64)",
                fontSize: { xs: "1rem", sm: "1.12rem", md: "1.2rem" },
                lineHeight: 1.65,
              }}
            >
              Nada de manuales eternos. Mirá el video, abrí tu panel y repetí el paso en tu negocio.
              Cuando terminás el recorrido, ya entendés cómo vender y gestionar desde un solo lugar.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ mt: 3, width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={scrollToCourse}
                sx={{
                  minHeight: 56,
                  px: 3.2,
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  fontWeight: 950,
                  textTransform: "none",
                  boxShadow: "0 16px 34px rgba(243,90,58,.28)",
                  "&:hover": { bgcolor: BRAND.orangeDark },
                }}
              >
                Empezar el curso
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<DashboardRoundedIcon />}
                onClick={openPanel}
                disabled={loading}
                sx={{
                  minHeight: 56,
                  px: 3,
                  borderRadius: 999,
                  color: BRAND.dark,
                  borderColor: "rgba(25,36,47,.2)",
                  fontWeight: 900,
                  textTransform: "none",
                }}
              >
                {primaryLabel}
              </Button>
            </Stack>

            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              spacing={1}
              sx={{ mt: 2.6 }}
            >
              {["Videos cortos", "Pasos simples", "Sin tecnicismos", "Aplicalo mientras mirás"].map((label) => (
                <Stack key={label} direction="row" spacing={0.55} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: BRAND.green, fontSize: 17 }} />
                  <Typography sx={{ color: "rgba(25,36,47,.58)", fontSize: ".82rem", fontWeight: 800 }}>
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        component="section"
        id="curso"
        sx={{ scrollMarginTop: 92, px: { xs: 2, md: 4 }, pb: { xs: 6, md: 10 } }}
      >
        <Container maxWidth="lg" disableGutters>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.65fr) minmax(300px, .75fr)" },
              gap: { xs: 2.2, lg: 3 },
              alignItems: "start",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                overflow: "hidden",
                borderRadius: { xs: 3.5, md: 5 },
                border: "1px solid rgba(25,36,47,.1)",
                bgcolor: "#11171D",
                boxShadow: "0 28px 70px rgba(25,36,47,.16)",
              }}
            >
              <Box sx={{ position: "relative", width: "100%", pt: "56.25%" }}>
                <Box
                  component="iframe"
                  src={COURSE_EMBED_URL}
                  title="Curso rápido de WebProfit en YouTube"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                />
              </Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.4}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ px: { xs: 2, sm: 2.5 }, py: 2, bgcolor: "#19242F" }}
              >
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 950 }}>Tu taller está acá.</Typography>
                  <Typography sx={{ mt: 0.25, color: "rgba(255,255,255,.6)", fontSize: ".82rem" }}>
                    Usá la lista del reproductor para avanzar por todos los videos.
                  </Typography>
                </Box>
                <Button
                  href={COURSE_YOUTUBE_URL}
                  target="_blank"
                  rel="noreferrer"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ color: "#FFB39F", fontWeight: 900, textTransform: "none", flexShrink: 0 }}
                >
                  Abrir en YouTube
                </Button>
              </Stack>
            </Paper>

            <Stack spacing={1.05}>
              <Typography sx={{ color: BRAND.orange, fontWeight: 950, fontSize: ".76rem", letterSpacing: ".09em", textTransform: "uppercase" }}>
                Elegí tu objetivo
              </Typography>
              {paths.map((item) => (
                <CoursePathButton
                  key={item.id}
                  item={item}
                  active={item.id === activePath.id}
                  onClick={() => setActivePathId(item.id)}
                />
              ))}
            </Stack>
          </Box>

          <Paper
            elevation={0}
            sx={{
              mt: { xs: 2.2, md: 3 },
              p: { xs: 2.2, sm: 3, md: 4 },
              borderRadius: { xs: 3.5, md: 5 },
              bgcolor: BRAND.soft,
              border: "1px solid rgba(25,36,47,.08)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(320px, .78fr)" },
                gap: { xs: 2.5, md: 5 },
                alignItems: "center",
              }}
            >
              <Box>
                <Typography sx={{ color: BRAND.orange, fontWeight: 950, fontSize: ".75rem", textTransform: "uppercase", letterSpacing: ".08em" }}>
                  Tu próximo paso
                </Typography>
                <Typography
                  component="h2"
                  sx={{ mt: 0.8, fontWeight: 950, letterSpacing: "-.04em", lineHeight: 1.08, fontSize: { xs: "1.7rem", md: "2.4rem" } }}
                >
                  {activePath.title}
                </Typography>
                <Typography sx={{ mt: 1.1, maxWidth: 650, color: "rgba(25,36,47,.62)", lineHeight: 1.65 }}>
                  {activePath.description}
                </Typography>
              </Box>

              <Stack spacing={1.1}>
                {activePath.steps.map((step, index) => (
                  <Stack
                    key={step}
                    direction="row"
                    spacing={1.2}
                    alignItems="center"
                    sx={{ p: 1.35, borderRadius: 2.8, bgcolor: "#fff", border: "1px solid rgba(25,36,47,.07)" }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(243,90,58,.1)",
                        color: BRAND.orangeDark,
                        fontWeight: 950,
                        fontSize: ".78rem",
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography sx={{ color: "rgba(25,36,47,.78)", fontWeight: 800, fontSize: ".9rem", lineHeight: 1.4 }}>
                      {step}
                    </Typography>
                  </Stack>
                ))}
                <Button
                  variant="contained"
                  size="large"
                  onClick={openPanel}
                  disabled={loading}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    mt: "4px !important",
                    minHeight: 54,
                    borderRadius: 3,
                    bgcolor: BRAND.dark,
                    fontWeight: 950,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#243442" },
                  }}
                >
                  {isAuthenticated ? activePath.action : "Crear mi cuenta y hacerlo"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Box component="section" sx={{ px: { xs: 2, md: 4 }, py: { xs: 6, md: 9 }, bgcolor: "#19242F", color: "#fff" }}>
        <Container maxWidth="lg" disableGutters>
          <Stack alignItems="center" textAlign="center">
            <Inventory2RoundedIcon sx={{ color: "#FF9D84", fontSize: 34 }} />
            <Typography
              component="h2"
              sx={{ mt: 1.6, maxWidth: 760, fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.05, fontSize: { xs: "2rem", md: "3.2rem" } }}
            >
              No necesitás aprender el sistema entero para empezar a usarlo bien.
            </Typography>
            <Typography sx={{ mt: 1.5, maxWidth: 650, color: "rgba(255,255,255,.64)", lineHeight: 1.65 }}>
              Mirá lo que necesitás hoy, aplicalo en tu negocio y volvé al curso cuando quieras avanzar al siguiente nivel.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3, width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="contained"
                size="large"
                onClick={openPanel}
                disabled={loading}
                startIcon={isAuthenticated ? <DashboardRoundedIcon /> : <RocketLaunchRoundedIcon />}
                sx={{ minHeight: 56, px: 3.2, borderRadius: 999, bgcolor: BRAND.orange, fontWeight: 950, textTransform: "none", "&:hover": { bgcolor: BRAND.orangeDark } }}
              >
                {primaryLabel}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<WhatsAppIcon />}
                onClick={() => (window.location.href = WHATSAPP_URL)}
                sx={{ minHeight: 56, px: 3, borderRadius: 999, color: "#fff", borderColor: "rgba(255,255,255,.32)", fontWeight: 900, textTransform: "none", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.06)" } }}
              >
                Me trabé, necesito ayuda
              </Button>
            </Stack>
            <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 2.2, color: "rgba(255,255,255,.48)" }}>
              <SupportAgentRoundedIcon sx={{ fontSize: 17 }} />
              <Typography sx={{ fontSize: ".78rem" }}>Aprender, aplicar, vender. Sin vueltas.</Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
